import { Extension, Node } from '@tiptap/core';
import { Fragment, Node as ProseMirrorNode, Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { v4 as uuidv4 } from 'uuid';
import { DOCUMENT_HTML_ATTRIBUTES } from '../../constants';
import { idUsedEarlierInDocument } from '../../largeDocument/editor/controller';
import { CAPTION_NODE_NAME } from '../nodes/caption/constants';
import { TAB_NODE_NAME } from '../nodes/tab/constants';
import { PAGE_BREAK_NODE_NAME } from '../nodes/pageBreak/constants';
import { ELEMENT_ROW_NODE_NAME } from '../nodes/elementRow/constants';
import { FORM_FIELD_NODE_NAME } from '../nodes/formField/constants';
import {
  TEMPLATE_FIELD_INLINE_NAME,
  TEMPLATE_FIELD_BLOCK_NAME,
  TEMPLATE_FIELD_DROPDOWN_NAME,
  TEMPLATE_FIELD_NODE_TYPES,
} from '../../doc-engine/templateFields';
import mapFragment from '../utils/mapFragment';
import { markSystemTr } from '../plugins/shared';

/**
 * One id a node carries. Every id here must be unique within a document, and is given a fresh value
 * both when content is duplicated and when a duplicate is found in the document.
 */
export type TUniqueIdAttribute = {
  attr: 'id' | 'fieldId';
  htmlAttr: string;
  nodeTypes: string[];
  onRegenerate?: (previous: string | null, next: string) => void;
};

interface Options {
  idAttributes: TUniqueIdAttribute[];
}

export const UNIQUE_ID_NODE_TYPES = [
  'heading',
  'bulletList',
  'orderedListWithStart',
  'taskList',
  'taskItem',
  'listItem',
  'paragraph',
  'title',
  'reference',
  'reusable-block',
  'inline-block',
  'table',
  'tableRow',
  'tableHeader',
  'tableCell',
  'image',
  PAGE_BREAK_NODE_NAME,
  'section',
  CAPTION_NODE_NAME,
  TAB_NODE_NAME,
  ELEMENT_ROW_NODE_NAME,
  TEMPLATE_FIELD_INLINE_NAME,
  TEMPLATE_FIELD_BLOCK_NAME,
  TEMPLATE_FIELD_DROPDOWN_NAME,
  FORM_FIELD_NODE_NAME,
];

export const NODE_ID: TUniqueIdAttribute = {
  attr: 'id',
  htmlAttr: DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID,
  nodeTypes: UNIQUE_ID_NODE_TYPES,
};

// Every copy of a field is its own field. Linked fields, if ever built, would share a fieldId instead.
export const FIELD_ID: TUniqueIdAttribute = {
  attr: 'fieldId',
  htmlAttr: DOCUMENT_HTML_ATTRIBUTES.TEMPLATE_FIELD_ID,
  nodeTypes: TEMPLATE_FIELD_NODE_TYPES,
};

/** Both ids, in the order they are serialized into the stored html. */
export const ID_ATTRIBUTES = [FIELD_ID, NODE_ID];

const CustomUniqueId = Extension.create<Options>({
  name: 'custom-unique-id',

  addOptions() {
    return {
      idAttributes: [],
    };
  },

  addProseMirrorPlugins() {
    const { idAttributes } = this.options;
    let isInitialized = false;

    return [
      ...(this.parent?.() || []),

      new Plugin({
        key: new PluginKey('custom-unique-id'),

        appendTransaction: (transactions, oldState, newState) => {
          if (!this.editor.isEditable) return null;

          let tr = newState.tr;
          if (!transactions.some((t) => t.docChanged) && isInitialized) return null;
          isInitialized = true;

          const seen = idAttributes.map(() => new Set<string>());
          let modified = false;

          newState.doc.descendants((node, pos) => {
            let attrs: Record<string, unknown> | null = null;

            idAttributes.forEach((idAttribute, index) => {
              if (!idAttribute.nodeTypes.includes(node.type.name)) return;

              const value = node.attrs[idAttribute.attr];
              if (value && !seen[index].has(value) && !idUsedEarlierInDocument(this.editor, value)) {
                seen[index].add(value);
                return;
              }

              const next = uuidv4();
              idAttribute.onRegenerate?.(value ?? null, next);
              seen[index].add(next);
              attrs = { ...(attrs ?? node.attrs), [idAttribute.attr]: next };
            });

            if (!attrs) return;
            tr = tr.setNodeMarkup(pos, undefined, attrs);
            modified = true;
          });

          if (!modified) return null;
          return markSystemTr(tr);
        },
      }),
    ];
  },
});

export default CustomUniqueId;

export const addUniqueIdAttribute = (extension: Node): Node =>
  extension.extend({
    addAttributes() {
      const ids = ID_ATTRIBUTES.filter(({ nodeTypes }) => nodeTypes.includes(this.name));
      // Silently dropping the attribute would discard every stored id on this node type on the next load.
      if (!ids.length) {
        throw new Error(
          `[CustomUniqueId] "${this.name}" is wrapped by addUniqueIdAttribute() but not in UNIQUE_ID_NODE_TYPES`
        );
      }

      return {
        ...this.parent?.(),
        ...Object.fromEntries(
          ids.map(({ attr, htmlAttr }) => [
            attr,
            {
              // No `isRequired`: in v3 it suppresses the attribute default, which makes every one of these
              // nodes non-generatable and the schema refuses to build.
              default: null,
              parseHTML: (element: HTMLElement) => element.getAttribute(htmlAttr),
              renderHTML: (attributes: Record<string, unknown>) => ({ [htmlAttr]: attributes[attr] }),
            },
          ])
        ),
      };
    },
  });

/** A node view draws its own wrapper, so the ids have to be written onto it by hand. */
export const syncIdAttributes = (dom: HTMLElement, node: ProseMirrorNode): void => {
  ID_ATTRIBUTES.forEach(({ attr, htmlAttr, nodeTypes }) => {
    if (!nodeTypes.includes(node.type.name)) return;
    const value = node.attrs[attr];
    // An update can clear the value - drop the attribute rather than leaving the old one behind.
    if (value) dom.setAttribute(htmlAttr, value);
    else dom.removeAttribute(htmlAttr);
  });
};

/** Duplicated content is a new thing: every id it carries is replaced before it enters the document. */
export const uniqueIdPasteRule =
  (idAttributes: TUniqueIdAttribute[]) =>
  (fragment: Fragment, _: Schema): Fragment =>
    mapFragment(fragment, (node) => {
      const fresh: Record<string, string> = {};

      idAttributes.forEach((idAttribute) => {
        if (!idAttribute.nodeTypes.includes(node.type.name)) return;
        const next = uuidv4();
        idAttribute.onRegenerate?.(node.attrs[idAttribute.attr] ?? null, next);
        fresh[idAttribute.attr] = next;
      });

      if (!Object.keys(fresh).length) return node;
      return node.type.create({ ...node.attrs, ...fresh }, node.content, node.marks);
    });
